import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { CreditCard, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const PaymentModal = ({ open, onClose, rideData, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Check for payment completion on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdFromUrl = urlParams.get('session_id');
    
    if (sessionIdFromUrl && open) {
      setSessionId(sessionIdFromUrl);
      setProcessingPayment(true);
      pollPaymentStatus(sessionIdFromUrl);
    }
  }, [open]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setProcessingPayment(false);
      toast({
        title: 'Payment check timed out',
        description: 'Please check your ride history',
        variant: 'destructive',
      });
      return;
    }

    try {
      const api = (await import('../utils/api')).default;
      const response = await api.get(`/payments/checkout/status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        toast({
          title: 'Payment successful!',
          description: 'Your ride has been confirmed',
        });
        setProcessingPayment(false);
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
        onClose();
        return;
      } else if (response.data.status === 'expired') {
        toast({
          title: 'Payment session expired',
          description: 'Please try again',
          variant: 'destructive',
        });
        setProcessingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setProcessingPayment(false);
      toast({
        title: 'Error',
        description: 'Failed to check payment status',
        variant: 'destructive',
      });
    }
  };

  const handlePayment = async () => {
    if (!rideData) {
      console.error('No ride data available');
      return;
    }

    console.log('Starting payment process for ride:', rideData.id);
    setLoading(true);
    
    try {
      const api = (await import('../utils/api')).default;
      
      // Get current URL for success/cancel redirects
      const currentUrl = window.location.origin + window.location.pathname;
      const successUrl = `${currentUrl}?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = currentUrl;

      console.log('Creating Stripe checkout session...');
      const response = await api.post('/payments/checkout/session', {
        amount: parseFloat(rideData.price),
        currency: 'usd',
        success_url: successUrl,
        cancel_url: cancelUrl,
        metadata: {
          ride_id: rideData.id || 'pending',
          pickup: rideData.pickup,
          dropoff: rideData.dropoff,
          ride_type: rideData.rideType?.name || 'Unknown'
        }
      });

      console.log('Stripe session response:', response.data);

      if (response.data && response.data.url) {
        console.log('Redirecting to Stripe URL:', response.data.url);
        // Small delay to ensure state updates
        setTimeout(() => {
          window.location.href = response.data.url;
        }, 100);
      } else {
        console.error('No redirect URL in response:', response.data);
        throw new Error('No payment URL received from server');
      }
    } catch (error) {
      console.error('Payment error:', error);
      console.error('Error details:', error.response?.data);
      
      toast({
        title: 'Payment failed',
        description: error.response?.data?.detail || error.message || 'Failed to initiate payment',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  if (processingPayment) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-pink-600" />
            <div className="text-center">
              <h3 className="text-lg font-semibold">Processing Payment</h3>
              <p className="text-sm text-gray-600 mt-2">
                Please wait while we confirm your payment...
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm Payment</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {rideData && (
            <>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Pickup:</span>
                  <span className="font-medium text-right ml-2">{rideData.pickup}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Dropoff:</span>
                  <span className="font-medium text-right ml-2">{rideData.dropoff}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Ride Type:</span>
                  <span className="font-medium">{rideData.rideType?.name}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex justify-between">
                    <span className="text-gray-900 font-semibold">Total:</span>
                    <span className="text-2xl font-bold text-gray-900">K{rideData.price}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={loading}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-5 w-5" />
                    Pay K{rideData.price}
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                You will be redirected to Stripe to complete your payment securely
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;